import {
	Avatar,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { userInfoAtom } from '~atoms';
import { InfoCard, InfoCardRowDef } from '~components';
import { useAuthorize } from '~features/authentication';
import {
	OneOffPaymentsService,
	Payment,
	PaymentStatusChip,
	PaymentTypeEnum,
	RefundStatusChip,
} from '~features/finances';
import { buildAdyenLogoSvgUrl } from '~utils';
import {
	dateLeadingZerosFormatter,
	datetimeLeadingZerosNoSecondsFormatter,
	datetimeNoSecondsFormatter,
} from '~utils/dateUtils';

const service = new OneOffPaymentsService();

interface BillingCardProps {
	bookingData?: object;
}

const BookingBillingCard = ({ bookingData }: BillingCardProps) => {
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuthorize();
	const userInfo = useAtomValue(userInfoAtom);

	const isProvider =
		userInfo?.organisation.id == bookingData?.hubReference.organisationReference.id;
	const isBusinessBilling = bookingData.billingType === 'business';

	const { data: oneOffPaymentData, isLoading: isOneOffPaymentsLoading } = useSWR(
		bookingData.paymentType === PaymentTypeEnum.OneOffPayment && isSuperAdmin() ?
			[service.basePath, { bookingId: bookingData.id }]
		:	null,
		([_, arg]) => service.getOneOffPayments(arg)
	);

	const transactions = oneOffPaymentData?.results.flatMap((payment) => {
		const refunds = payment.refunds?.map((el) => ({
			dateCreated: payment.dateCreated,
			type: 'refund',
			paymentMethodType: payment.paymentMethodType,
			primaryAccountNumber: payment.primaryAccountNumber,
			...el,
		}));

		return [{ type: 'payment', ...payment }, ...refunds];
	});

	const rows: InfoCardRowDef[] =
		bookingData != null ?
			[
				{
					headerName: t('views.planboard.addBooking.usage.sectionTitle'),
					value: isBusinessBilling,
					valueFormatter: (value: boolean) =>
						t(
							value ?
								'views.bookingDetail.invoicesBiling.organizational'
							:	'views.bookingDetail.invoicesBiling.personal'
						),
				},
				{
					headerName: t('views.bookingDetail.invoicesBiling.billingPeriod'),
					value: bookingData.billingPeriodReference,
					valueFormatter: (value?: object) =>
						value != null ?
							`${dateLeadingZerosFormatter.format(new Date(value.periodStart))} - ${dateLeadingZerosFormatter.format(new Date(value.periodEnd))}`
						:	'-',
					hidden: bookingData.paymentType === PaymentTypeEnum.OneOffPayment,
				},
				{
					headerName: t('paymentType'),
					value: bookingData.paymentType,
					valueFormatter: (value?: PaymentTypeEnum) =>
						value != null ?
							t(value === PaymentTypeEnum.BillingPeriod ? 'ui.label.billingPeriod' : value)
						:	'-',
					hidden: isBusinessBilling,
				},
				{
					headerName: t('ui.organisation'),
					value: bookingData.userReference.organisationReference.name,
					hidden: !(isSuperAdmin() && isBusinessBilling),
				},
				{
					headerName: t('ui.billing.lable'),
					value: bookingData.billingPeriodReference?.paymentMethodReference,
					renderCell: (value?: any) =>
						value != null ?
							<Stack spacing={1} direction='row' sx={{ alignItems: 'center' }}>
								<img src={value.logo} style={{ maxHeight: 36 }} />
								<Typography sx={{ fontSize: 12 }}>{value.name}</Typography>
							</Stack>
						:	'-',
					// hidden: !(isSuperAdmin() && !isBusinessBilling ||),
					hidden:
						!isSuperAdmin() ||
						isBusinessBilling ||
						bookingData.paymentType === PaymentTypeEnum.OneOffPayment,
					paddingY: 0,
				},
				{
					headerName: t('views.bookingDetail.invoicesBiling.card'),
					value: bookingData.billingPeriodReference?.paymentMethodReference,
					valueFormatter: (value?: any) => value?.bank?.iban ?? value?.card?.number,
					hidden:
						!isSuperAdmin() ||
						isBusinessBilling ||
						bookingData.paymentType === PaymentTypeEnum.OneOffPayment,
					// hidden: !(isSuperAdmin() && !isBusinessBilling),
				},

				// Prices
				{
					headerName: `${t('views.bookingDetail.invoicesBiling.reservationPricing')}  (${t('ui.incl')}. ${bookingData.price?.vatPercentage.toFixed(2)}% ${t('ui.vat')})`,
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.priceBreakdownIncludingVat != null ?
							`${value.currencySymbol}${value.priceBreakdownIncludingVat?.initialDuration.toFixed(2)}`
						:	'-',
				},
				{
					headerName: `${t('views.bookingDetail.invoicesBiling.overtime')} (${t('ui.incl')}. ${bookingData.price?.vatPercentage?.toFixed(2)}% ${t('ui.vat')})`,
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.priceBreakdownIncludingVat != null ?
							`${value.currencySymbol}${value.priceBreakdownIncludingVat?.extraDuration.toFixed(2)}`
						:	'-',
					hidden:
						bookingData.status !== 'overtime' &&
						(bookingData?.price?.priceBreakdownIncludingVat == null ||
							bookingData?.price?.priceBreakdownIncludingVat?.extraDuration <= 0),
				},
				{
					headerName: `${t('views.bookingDetail.invoicesBiling.chargedkms')} (${t('ui.incl')}. ${bookingData.price?.vatPercentage?.toFixed(2)}% ${t('ui.vat')})`,
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.priceBreakdownIncludingVat != null ?
							`${value.currencySymbol}${value.priceBreakdownIncludingVat?.extraDistance.toFixed(2)}`
						:	'-',
					hidden:
						bookingData?.price?.priceBreakdownIncludingVat == null ||
						bookingData?.price?.priceBreakdownIncludingVat?.extraDistance <= 0,
				},
				{
					headerName: t('views.bookingDetail.invoicesBiling.promoDiscount'),
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.priceBreakdownIncludingVat != null ?
							`${value.currencySymbol}${value.priceBreakdownIncludingVat?.codeDiscount.toFixed(2)}`
						:	'-',
					hidden:
						bookingData?.price?.priceBreakdownIncludingVat == null ||
						bookingData?.price?.priceBreakdownIncludingVat?.codeDiscount <= 0,
				},
				{
					headerName: t('views.bookingDetail.invoicesBiling.adminDiscount'),
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.priceBreakdownIncludingVat != null ?
							`${value.currencySymbol}${value.priceBreakdownIncludingVat?.adminDiscount.toFixed(2)}`
						:	'-',
					hidden:
						bookingData?.price?.priceBreakdownIncludingVat == null ||
						bookingData?.price?.priceBreakdownIncludingVat?.adminDiscount <= 0,
				},
				{
					headerName: t('ui.refund'),
					value: bookingData?.price,
					valueFormatter: (value?: object) =>
						value.total?.refund != null ?
							`-${value.currencySymbol}${value.total?.refund?.toFixed(2)}`
						:	'-',
					hidden: bookingData?.price?.total == null || bookingData?.price?.total?.refund <= 0,
				},
				{
					headerName: `${t('ui.label.total')} (${t('ui.incl')} ${bookingData.price?.vatPercentage.toFixed(2)}% ${t('ui.vat')})`,
					value: bookingData.price,
					valueFormatter: (value?: object) =>
						value.total?.total != null ?
							`${value.currencySymbol}${value.total.total.toFixed(2)}`
						:	'-',
					hidden: bookingData.price?.total?.total <= 0,
				},
				{
					headerName: isProvider && bookingData.billingType === 'personal' ? 'Date' : 'Due date',
					value: bookingData?.billingPeriodReference?.periodEnd,
					valueFormatter: (value?: string) =>
						value != null ? datetimeLeadingZerosNoSecondsFormatter.format(new Date(value)) : '-',
					hidden: bookingData.paymentType === PaymentTypeEnum.OneOffPayment,
				},
				{
					value: transactions,
					renderRow: (value?: Payment[]) => (
						<Stack>
							<Typography variant='h5'>{t('transactions')}</Typography>
							{isOneOffPaymentsLoading ?
								<OneOffPaymentSkeleton />
							:	<List sx={{ width: 1, px: 0 }}>
									{value
										?.sort((a, b) => a.dateModified.valueOf() - b.dateModified.valueOf())
										.map((el, i) => (
											<ListItem
												key={i}
												secondaryAction={
													<Stack sx={{ alignItems: 'flex-end' }}>
														<Typography
															sx={{ fontSize: 12 }}
														>{`${el.type === 'refund' ? '-' : ''}€${el.total.toFixed(2)}`}</Typography>
														{el.type === 'refund' ?
															<RefundStatusChip status={el.status} />
														:	<PaymentStatusChip status={el.status} />}
													</Stack>
												}
												sx={{
													px: 0,
													'& .MuiListItemSecondaryAction-root': {
														right: 0,
													},
													minHeight: 67,
												}}
											>
												{el.paymentMethodType != null && (
													<ListItemAvatar>
														<Avatar
															src={buildAdyenLogoSvgUrl(el.paymentMethodType)}
															variant='square'
															slotProps={{
																img: {
																	style: {
																		objectFit: 'contain',
																	},
																},
															}}
														/>
													</ListItemAvatar>
												)}
												<ListItemText
													inset={el.paymentMethodType == null}
													primary={
														el.primaryAccountNumber?.truncated != null ?
															`*** ${el.primaryAccountNumber.truncated}`
														:	el.email
													}
													secondary={datetimeNoSecondsFormatter.format(el.dateModified)}
													sx={{ '& .MuiListItemText-primary': { fontSize: '12px' } }}
												/>
											</ListItem>
										))}
								</List>
							}
						</Stack>
					),
					hidden: !isSuperAdmin() || !oneOffPaymentData,
				},
			]
		:	[];

	return (
		<InfoCard
			title={t('views.userDetails.summary.cardHeaders.invoicesAndBilling')}
			statusComponent={
				bookingData.billingPeriodReference?.status != null && (
					<PaymentStatusChip
						status={bookingData.billingPeriodReference?.status}
						// status={
						// 	bookingData.paymentStatus === 'active' ?
						// 		PaymentStatusEnum.Open
						// 	:	bookingData.paymentStatus
						// }
					/>
				)
			}
			rows={rows}
		/>
	);
};

const OneOffPaymentSkeleton = () => {
	return (
		<Stack spacing={1} sx={{ pt: 1 }}>
			{Array.from({ length: 2 }, (_, i) => i).map((_, i) => (
				<Stack key={i} direction='row' spacing={5} sx={{ alignItems: 'center', display: 'flex' }}>
					<Skeleton variant='circular' animation='wave' width={20} height={20} />
					<Skeleton animation='wave' sx={{ flexGrow: 1 }} />
				</Stack>
			))}
		</Stack>
	);
};

export default BookingBillingCard;
